import Fuse from 'fuse.js';
import { ReactNode } from 'react';

import { FilterOption } from '@/plp/components/PlpFilters/PlpFilters.type';
import { generateFilterObjects, getFuelSubTypeLabelAndSlug } from '@/plp/components/PlpFilters/plpFiltersMap';
import { formatMakeModelToUrlParam } from '@/plp/helpers/formatMakeModelToUrlParam';
import { PLP_BASE_PATH } from '@/plp/helpers/getPlpSlug';
import { FilterListPayload } from '@/types/CarFilters.types';

type IndexType =
  | 'make'
  | 'model'
  | 'bodyType'
  | 'fuelType'
  | 'keyword'
  | 'highlightedFeatures'
  | 'driveType'
  | 'transmission'
  | 'car'; // Special case to handle recent car search

export interface IndexItem {
  key: string | number;
  value: string;
  label: string;
  type: IndexType;
  order: number;
  matchStrength: number;
  url: string;
}

export const parseType = (type: string) => {
  switch (type) {
    case 'make':
      return 'Make';
    case 'model':
      return 'Model';
    case 'bodyType':
      return 'Body type';
    case 'fuelType':
      return 'Fuel type';
    case 'highlightedFeatures':
      return 'Feature';
    case 'driveType':
      return 'Drivetrain';
    case 'transmission':
      return 'Transmission';
    default:
      console.warn(`Unknown type: ${type}`);
      return '';
  }
};

const generateFilterUrl = (filterOptions: FilterOption[], filterKey: string) => {
  const filterSlug = filterOptions.find((option) => option.value === filterKey)?.slug ?? '';
  return `${PLP_BASE_PATH}/${filterSlug}`;
};

export const genFuseFilterIndex = (res: FilterListPayload) => {
  const filterOptions = generateFilterObjects(res);
  const make: IndexItem[] = res.makeAndModel.map((item) => ({
    key: `make-${item.key}`,
    value: `${item.key}`,
    label: item.label,
    type: 'make' as IndexType,
    order: 1,
    url: generateFilterUrl(filterOptions, item.key),
    matchStrength: 0,
  }));

  const models: IndexItem[] = [];
  res.makeAndModel.forEach((make) => {
    make.models?.forEach((model) => {
      // Skip the "All" in the models listing
      if (model.label === 'All') {
        return;
      }

      const urlParam = formatMakeModelToUrlParam(make.label, model.label);

      if (urlParam) {
        models.push({
          key: `model-${make.key}-${model.key}`,
          value: `${model.key}`,
          label: `${make.label} ${model.label}`,
          type: 'model' as IndexType,
          order: 2,
          url: generateFilterUrl(filterOptions, model.key),
          matchStrength: 0,
        });
      }
    });
  });

  const bodyType: IndexItem[] = res.bodyType.map((item) => ({
    key: `bodyType-${item.key}`,
    value: `${item.key}`,
    label: item.label,
    type: 'bodyType' as IndexType,
    order: 3,
    url: generateFilterUrl(filterOptions, item.key),
    matchStrength: 0,
  }));

  const fuelCategories: IndexItem[] = res.fuelType.map((item) => ({
    key: `fuelType-${item.key}`,
    value: `${item.key}`,
    label: item.label,
    type: 'fuelType' as IndexType,
    order: 3,
    url: generateFilterUrl(filterOptions, String(item.key)),
    matchStrength: 0,
  }));

  const fuelSubTypes: IndexItem[] = [];
  res.fuelType.forEach((category) => {
    category.subTypes?.forEach((sub) => {
      if (sub.label === 'All') return;
      fuelSubTypes.push({
        key: `fuelType-${category.key}-${sub.key}`,
        value: `${sub.key}`,
        label: getFuelSubTypeLabelAndSlug(category.label, sub.label).label,
        type: 'fuelType' as IndexType,
        order: 3,
        url: generateFilterUrl(filterOptions, String(sub.key)),
        matchStrength: 0,
      });
    });
  });

  const highlightedFeatures = res.highlightedFeatures.map((item) => ({
    key: `highlightedFeatures-${item.key}`,
    value: `${item.key}`,
    label: item.label,
    type: 'highlightedFeatures' as IndexType,
    order: 3,
    url: generateFilterUrl(filterOptions, String(item.key)),
    matchStrength: 0,
  }));

  const driveTypes = res.driveType.map((item) => ({
    key: `driveTypes-${item.key}`,
    value: `${item.key}`,
    label: item.label,
    type: 'driveType' as IndexType,
    order: 3,
    url: generateFilterUrl(filterOptions, String(item.key)),
    matchStrength: 0,
  }));

  const transmissions = res.transmission.map((item) => ({
    key: `transmissions-${item.key}`,
    value: `${item.key}`,
    label: item.label,
    type: 'transmission' as IndexType,
    order: 3,
    url: generateFilterUrl(filterOptions, String(item.key)),
    matchStrength: 0,
  }));

  const collection = [
    ...bodyType,
    ...fuelCategories,
    ...fuelSubTypes,
    ...make,
    ...models,
    ...highlightedFeatures,
    ...driveTypes,
    ...transmissions,
  ];

  const options = {
    includeMatches: true,
    includeScore: true,
    threshold: 0.05,
    ignoreLocation: true,
    keys: ['label'],
  };
  const fuse = new Fuse(collection, options);
  return fuse;
};

/**
 * Recursive highlight function
 */
export const highlight = (value: string, indices: readonly Fuse.RangeTuple[] = [], i = 1): ReactNode => {
  const pair = indices[indices.length - i];
  if (!pair) {
    return value;
  }
  // Skip highlight when it's only one character
  return pair[0] === pair[1] ? (
    <>
      {highlight(value.substring(0, pair[0]), indices, i + 1)}
      {value.substring(pair[1])}
    </>
  ) : (
    <>
      {highlight(value.substring(0, pair[0]), indices, i + 1)}
      <mark className="bg-transparent font-extrabold">{value.substring(pair[0], pair[1] + 1)}</mark>
      {value.substring(pair[1] + 1)}
    </>
  );
};
